import React, { useState } from 'react';
import { Globe, Search, AlertCircle, CheckCircle, X, Eye, ExternalLink } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/apiRouting';
import { useAuth } from '../../contexts/AuthContext';
import MenuReview from './MenuReview';

interface WebsiteScrapingProps {
  enterpriseId: string;
  onJobCreated?: (jobId: string) => void;
  onClose?: () => void;
  onBack?: () => void;
}

interface ScrapingJob {
  url: string;
  status: 'pending' | 'scraping' | 'success' | 'error';
  jobId?: string;
  error?: string;
  itemCount?: number;
}

const WebsiteScraping: React.FC<WebsiteScrapingProps> = ({
  enterpriseId,
  onJobCreated,
  onClose,
  onBack
}) => {
  const { getIdToken } = useAuth();
  const [url, setUrl] = useState('');
  const [jobs, setJobs] = useState<ScrapingJob[]>([]);
  const [isValidUrl, setIsValidUrl] = useState(false);
  const [reviewingJobId, setReviewingJobId] = useState<string | null>(null);

  const validateUrl = (inputUrl: string) => {
    try {
      const urlObj = new URL(inputUrl);
      const isValid = urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
      setIsValidUrl(isValid);
      return isValid;
    } catch {
      setIsValidUrl(false);
      return false;
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputUrl = e.target.value;
    setUrl(inputUrl);
    
    if (inputUrl.trim()) {
      validateUrl(inputUrl.trim());
    } else {
      setIsValidUrl(false);
    }
  };

  const handleScrape = async () => {
    if (!url.trim() || !isValidUrl) return;

    const newJob: ScrapingJob = {
      url: url.trim(),
      status: 'scraping'
    };

    setJobs(prev => [...prev, newJob]);
    
    try {
      const token = await getIdToken();
      const response = await fetch(API_ENDPOINTS.menuSetupScrapeWebsite(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          enterpriseId,
          sourceType: 'url',
          url: url.trim()
        })
      });

      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.success) {
        throw new Error(result?.message || result?.error || `Scraping failed (${response.status})`);
      }

      const jobId: string = result?.data?.jobId;
      const itemCount: number = result?.data?.extractedItemCount ?? result?.data?.extractedItems?.length ?? 0;
      setJobs(prev => prev.map(job =>
        job.url === url.trim() ? {
          ...job,
          status: 'success',
          jobId,
          itemCount
        } : job
      ));

      if (onJobCreated && jobId) {
        onJobCreated(jobId);
      }

      // Clear the URL input
      setUrl('');
      setIsValidUrl(false);

    } catch (error) {
      setJobs(prev => prev.map(job => 
        job.url === url.trim() ? {
          ...job,
          status: 'error',
          error: error instanceof Error ? error.message : 'Scraping failed'
        } : job
      ));
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scraping':
        return <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent" />;
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Globe className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scraping':
        return 'border-blue-200 bg-blue-50';
      case 'success':
        return 'border-green-200 bg-green-50';
      case 'error':
        return 'border-red-200 bg-red-50';
      default:
        return 'border-gray-200 bg-white';
    }
  };

  const getDomainFromUrl = (url: string) => {
    try {
      return new URL(url).hostname;
    } catch {
      return url;
    }
  };

  const exampleUrls = [
    'https://restaurant.com/menu',
    'https://yourrestaurant.com/food-menu',
    'https://example-restaurant.com/our-menu'
  ];

  const tips = [
    'Make sure the URL contains your complete menu',
    'Pages with tables or structured layouts work best',
    'PDFs hosted on websites can also be scraped',
    'Private or password-protected pages are not supported'
  ];

  // If reviewing a job, show the MenuReview component
  if (reviewingJobId) {
    return (
      <MenuReview 
        jobId={reviewingJobId} 
        onBack={() => setReviewingJobId(null)}
      />
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Globe className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                Import from Website
              </h2>
              <p className="text-gray-600">
                Provide your restaurant website URL and we'll automatically extract menu items from your online menu.
              </p>
            </div>
          </div>
          {(onClose || onBack) && (
            <button
              onClick={onClose || onBack}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* URL Input */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Website URL
        </label>
        <div className="flex space-x-3">
          <div className="flex-1">
            <input
              type="url"
              value={url}
              onChange={handleUrlChange}
              placeholder="https://your-restaurant.com/menu"
              className={`
                w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors
                ${isValidUrl ? 'border-green-300 bg-green-50' : url.trim() ? 'border-red-300 bg-red-50' : 'border-gray-300'}
              `}
            />
            {url.trim() && !isValidUrl && (
              <p className="text-sm text-red-600 mt-2 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                Please enter a valid URL (must start with http:// or https://)
              </p>
            )}
            {isValidUrl && (
              <p className="text-sm text-green-600 mt-2 flex items-center">
                <CheckCircle className="w-4 h-4 mr-1" />
                Valid URL format
              </p>
            )}
          </div>
          <button
            onClick={handleScrape}
            disabled={!url.trim() || !isValidUrl}
            className={`
              px-6 py-3 rounded-lg font-medium transition-all flex items-center space-x-2 min-w-[140px]
              ${url.trim() && isValidUrl
                ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-lg hover:shadow-xl'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }
            `}
          >
            <Search className="w-4 h-4" />
            <span>Scrape Menu</span>
          </button>
        </div>
      </div>

      {/* Examples */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
          <ExternalLink className="w-5 h-5 mr-2 text-blue-600" />
          Example URLs
        </h3>
        <div className="grid grid-cols-1 gap-2">
          {exampleUrls.map((exampleUrl, index) => (
            <button
              key={index}
              onClick={() => {
                setUrl(exampleUrl);
                validateUrl(exampleUrl);
              }}
              className="block w-full text-left p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors group"
            >
              <span className="text-blue-600 group-hover:text-blue-800 font-medium">{exampleUrl}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tips */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-4 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2" />
            Tips for Best Results
          </h3>
          <ul className="space-y-3">
            {tips.map((tip, index) => (
              <li key={index} className="flex items-start">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <span className="text-blue-800">{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Scraping Jobs */}
      {jobs.length > 0 && (
        <div className="mb-6">
          <h3 className="font-semibold text-gray-900 mb-4">
            Scraping Results
          </h3>
          <div className="space-y-3">
            {jobs.map((job, index) => (
              <div
                key={index}
                className={`
                  border rounded-lg p-4 flex items-center justify-between
                  ${getStatusColor(job.status)}
                `}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                    <Globe className="w-5 h-5 text-gray-500" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {getDomainFromUrl(job.url)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {job.url}
                    </p>
                    {job.error && (
                      <p className="text-sm text-red-600 mt-1">
                        {job.error}
                      </p>
                    )}
                    {job.status === 'success' && job.itemCount && (
                      <p className="text-sm text-green-600 mt-1">
                        Found {job.itemCount} menu items
                      </p>
                    )}
                    {job.jobId && (
                      <p className="text-sm text-gray-600 mt-1">
                        Job ID: {job.jobId}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {getStatusIcon(job.status)}
                  <a
                    href={job.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1 text-gray-500 hover:text-blue-500 transition-colors"
                    title="Open website"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  {job.status === 'success' && job.jobId && (
                    <button
                      onClick={() => setReviewingJobId(job.jobId!)}
                      className="p-1 text-blue-500 hover:text-blue-700 transition-colors"
                      title="Review extracted items"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* How it Works */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="font-semibold text-gray-900 mb-6">How Website Scraping Works</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Globe className="w-8 h-8 text-white" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">1. Analyze Page</h4>
            <p className="text-sm text-gray-600">
              Our AI analyzes your website structure to identify menu content
            </p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Search className="w-8 h-8 text-white" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">2. Extract Items</h4>
            <p className="text-sm text-gray-600">
              Extract menu items, prices, descriptions, and categories
            </p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">3. Review & Approve</h4>
            <p className="text-sm text-gray-600">
              Review extracted items and approve them for your menu
            </p>
          </div>
        </div>
      </div>

      {/* Success Actions */}
      {jobs.some(job => job.status === 'success') && (
        <div className="mt-6 text-center">
          <div className="bg-green-50 rounded-lg p-6">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-green-900 mb-2">
              Website Scraped Successfully!
            </h3>
            <p className="text-green-700 mb-4">
              Your menu items have been extracted and are ready for review.
            </p>
            <button
              onClick={() => {
                const successfulJob = jobs.find(job => job.status === 'success');
                if (successfulJob?.jobId) {
                  setReviewingJobId(successfulJob.jobId);
                }
              }}
              className="bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-6 rounded-lg transition-colors"
            >
              Review Extracted Items
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsiteScraping;